import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import {
  DefaultAdRequestSourceManager,
  AdRequestSourceManager
} from 'core/adRequestSource/AdRequestSourceManager';
import { AddonFeatureManager } from 'core';
import {
  CreativeApprovalConfig,
  CreativeType,
  isNative,
  isOneForAllCreativeType
} from 'core/creative/Creative';
import {
  DefaultCreativeManager,
  CreativeManager
} from 'core/creative/CreativeManager';
import _ from 'lodash';
import {
  SessionStorageHelper,
  SessionStorageItemKeys
} from 'helper/StorageHelper';
import { ROUTE_PATH } from 'enum/RoutePath';
import {
  CreativeFormBasicData,
  CreativeFormData,
  FormContentModel
} from './FlowSteps/SubSteps/FormContent/FormContentModel';
import {
  ComboSummaryModel,
  CreateImageMultipleSummaryModel,
  CreativeSummaryModel,
  HTML5SummaryModel,
  ImageSummaryModel,
  NativeSummaryModel,
  PPSSummaryModel,
  VideoSummaryModel,
  CustomLayoutSummaryModel,
  SandboxOutdoorSummaryModel,
  OutdoorLineMessageSummaryModel,
  LineMessageSummaryModel,
  DynamicOutdoorSummaryModel
} from './FlowSteps/SubSteps/SummaryContent/CreativeSummaryModel';
import { CreativeSetupFlowDataContextType } from './CreativeSetupFlowDataContext';
import {
  CreateCreativeAndBindSummaryStepModel,
  CreateCreativeSummaryStepModel,
  CreativeSummaryStepModel,
  EditCreativeSummaryStepModel
} from './FlowSteps/CreativeSummaryStepModel';
import {
  CreateImageFormModel,
  ImageFormModel
} from './FlowSteps/SubSteps/FormContent/ImageFormModel';
import {
  CreativeSetupStepModel,
  DefaultCreativeSetupStepModel
} from './FlowSteps/CreativeSetupStepModel';
import {
  AdvertiserManager,
  DefaultAdvertiserManager
} from 'core/advertiser/AdvertiserManager';
import { computeChecksumMd5 } from 'utils/Md5Utils';
import { DefaultOrderManager, OrderManager } from 'core/order/OrderManager';
import { RtbCampaign } from 'core/rtbCampaign/RtbCampaign';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import { DraftManager, RtbCampaignDraftManager } from 'core/draft/DraftManager';
import { NativeFormModel } from './FlowSteps/SubSteps/FormContent/NativeFormModel';
import { VideoFormModel } from './FlowSteps/SubSteps/FormContent/VideoFormModel';
import { PPSFormModel } from './FlowSteps/SubSteps/FormContent/PPSFormModel';
import { Html5FormModel } from './FlowSteps/SubSteps/FormContent/Html5FormModel';
import { ComboFormModel } from './FlowSteps/SubSteps/FormContent/ComboFormModel';
import { CtvFormModel } from './FlowSteps/SubSteps/FormContent/CtvFormModel';
import { AdLogo } from 'core/adLogo/AdLogo';
import { CustomLayoutFormModel } from './FlowSteps/SubSteps/FormContent/CustomLayoutFormModel';
import { Order } from 'core/order/Order';
import { Advertiser } from 'core/advertiser/Advertiser';
import { SandboxOutdoorFormModel } from './FlowSteps/SubSteps/FormContent/SandboxOutdoorFormModel';
import {
  DefaultL1ObjectManager,
  L1ObjectManager
} from 'core/l1Object/L1ObjectManager';
import {
  BindCreativeStateContentModel,
  DefaultBindCreativeStateContentModel
} from 'components/CampaignCreativeManagement/BindCreativeStateContentModel';
import { toast } from 'react-toastify';
import { LineMessageFormModel } from './FlowSteps/SubSteps/FormContent/LineMessageFormModel';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import {
  getCompositeSettingInvalidHint,
  isCompositeSettingBlocked
} from 'core/channelGroup/compositeSettingGate';
import { DynamicOutdoorFormModel } from './FlowSteps/SubSteps/FormContent/DynamicOutdoorFormModel';
import { DynamicCreativeTypeSetting } from 'core/channelGroup/ChannelGroup';
import { L1Object } from 'core/l1Object/L1Object';

type RedirectData = {
  pathname: string;
  search?: string;
  state?: any;
};
export interface CreativeSetupFlowPageModel {
  readonly type: string;
  readonly state: CreativeSetupFlowState;
  readonly event: UpdateEventListener<CreativeSetupFlowPageModel>;
  readonly tenmaxCategories: Array<SelectOptions>;
  readonly advertisers: Array<SelectOptions>;
  readonly l1ObjectId?: string;
  readonly campaignId?: string;
  readonly orderNumber?: string;
  readonly draftId?: string;
  readonly supportedCreativeType: CreativeType[];
  readonly dataContenxt: CreativeSetupFlowDataContextType;
  readonly canChooseAdvertiser: boolean;
  readonly needSetupLimitation: boolean;
  readonly addonFeatureManager: AddonFeatureManager;
  readonly defaultAdLogo: AdLogo;
  readonly creativeManager: CreativeManager;
  setEnableAdLogo: (enable: boolean) => void;
  setCreative(creative: CreativeFormData): void;
  init(): void;
  getCreativeSummaryModel(
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void
  ): CreativeSummaryStepModel;
  onUnmount(handler): void;
  onChooseAdvertiser(advertiserId: number): Promise<void>;
  setRedirectData(redirectData?: RedirectData): void;
  setFinishedRedirectData(redirectData?: RedirectData): void;
  cancel(): void;
  getCreativeSetupStepModel(
    currentCreativeType: CreativeType,
    type: string,
    activeTab: number,
    goSubStep: (subStepIndex: number) => void,
    goLast: (() => void) | undefined,
    goNext: () => void,
    addonFeatureManager: AddonFeatureManager,
    registerValidateMethod: (validateMethod) => void
  ): CreativeSetupStepModel;
  addCache(key: string, data: any): void;
  getCache(key: string): any;
}

export type CreativeSetupFlowProps = {
  readonly model: CreativeSetupFlowPageModel;
};

export type CreativeSetupFlowState = {
  readonly loading: boolean;
  readonly redirectData?: RedirectData;
  readonly creative?: CreativeFormData;
  readonly finished: boolean;
  readonly enableAdLogo: boolean;
};

export abstract class DefaultCreativeSetupFlowPageModel implements CreativeSetupFlowPageModel {
  event: FireableUpdateEventListener<CreativeSetupFlowPageModel>;
  loading: boolean;
  redirectData?: RedirectData;
  initCreative: any;
  creative?: CreativeFormData;
  tenmaxCategories: Array<SelectOptions>;
  finished: boolean;
  creativeSetupStepModel?: CreativeSetupStepModel;
  nativeToBannertemplates: {
    [size: string]: { templatePath?: string; htmlContent?: string };
  } = {
    '300x250': {},
    '320x100': {},
    '300x600': {}
  };
  uploadedFiles: { [key: string]: string } = {};
  cache: { [key: string]: string } = {};
  enableAdLogo = false;
  advertiser?: Advertiser;
  validCreativeTypes: number[];
  contentFormModelCache: { [key: string]: FormContentModel } = {};
  l1Object?: L1Object;
  creativeApprovalConfigs: CreativeApprovalConfig[] = [];

  constructor(
    public canChooseAdvertiser: boolean,
    public advertisers: Array<SelectOptions>,
    public addonFeatureManager: AddonFeatureManager,
    public defaultAdLogo: AdLogo,
    public campaignId?: string,
    public orderNumber?: string,
    public l1ObjectId?: string,
    public draftId?: string,
    protected adRequestResourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager(),
    public creativeManager: CreativeManager = new DefaultCreativeManager(),
    protected advertiserManager: AdvertiserManager = new DefaultAdvertiserManager(),
    protected campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager(),
    protected l1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager()
  ) {
    this.event = new FireableUpdateEventListener<CreativeSetupFlowPageModel>();
    this.loading = false;
    this.tenmaxCategories = [];
    this.finished = false;
    this.getFormContentModelOfType = this.getFormContentModelOfType.bind(this);
    this.validCreativeTypes = this.creativeManager
      .getCreativeTypeOptions()
      .map((option) => option.value);
  }

  abstract get type();

  abstract init();

  get forPmp() {
    return false;
  }

  getInitCreativeType() {
    return this.supportedCreativeType[0] || CreativeType.IMAGE;
  }

  getUploadedFileData = async (file: File) => {
    const fileCheckSum = await computeChecksumMd5(file);
    return this.uploadedFiles[fileCheckSum];
  };

  addUploadedFilesData = async (file, url) => {
    const fileCheckSum = await computeChecksumMd5(file);
    this.uploadedFiles[fileCheckSum] = url;
  };

  async initL1Object() {
    if (!this.l1ObjectId) {
      return;
    }
    this.l1Object = await this.l1ObjectManager.getL1Object(this.l1ObjectId);
  }

  initNativeBannerTemplates = async () => {
    const templateUrls =
      await this.creativeManager.getCreativeNativeToBannerTemplate();
    await Promise.all(
      templateUrls.map(async (url) => {
        try {
          const regex = /Bannersize-Native-(.*)-template/;
          const matches = url.match(regex);
          if (matches && matches[1]) {
            const size = matches[1].replace('-', 'x');
            const response = await fetch(url);
            this.nativeToBannertemplates[size] = {
              templatePath: url,
              htmlContent: await response.text()
            };
          }
        } catch (e) {
          console.log(e);
        }
      })
    );
  };

  get dataContenxt(): CreativeSetupFlowDataContextType {
    return {
      creative: this.creative,
      initCreative: this.initCreative,
      advertisers: this.advertisers,
      enableAdLogo: this.enableAdLogo,
      forPmp: this.forPmp,
      tenmaxCategories: this.tenmaxCategories,
      supportedCreativeType: this.supportedCreativeType,
      advertiser: this.advertiser,
      setCreative: this.setCreative,
      setFinishedRedirectData: this.setFinishedRedirectData,
      getFormContentModelOfType: this.getFormContentModelOfType.bind(this),
      getSummaryModel: this.getSummaryModel.bind(this)
    };
  }

  get needSetupLimitation() {
    return true;
  }

  get supportedCreativeType() {
    return this.validCreativeTypes.reduce((acc, type) => {
      if (
        !isOneForAllCreativeType(type) &&
        this.creativeManager.isCreativeTypeSupport(
          type,
          this.addonFeatureManager
        )
      ) {
        acc.push(type);
      }
      return acc;
    }, [] as CreativeType[]);
  }

  setEnableAdLogo = (enableAdLogo: boolean) => {
    this.enableAdLogo = enableAdLogo;
    this.updateState(false);
  };

  getCreativeSetupStepModel(
    creativeType: CreativeType,
    type: string,
    activeTab: number,
    goSubStep: (subStepIndex: number) => void,
    goLast: (() => void) | undefined,
    goNext: () => void,
    addonFeatureManager: AddonFeatureManager,
    registerValidateMethod: (validateMethod) => any
  ): CreativeSetupStepModel {
    if (this.creativeSetupStepModel) {
      this.creativeSetupStepModel.updateProps(activeTab);
      return this.creativeSetupStepModel;
    }
    this.creativeSetupStepModel = new DefaultCreativeSetupStepModel(
      creativeType,
      type,
      activeTab,
      goSubStep,
      goLast,
      goNext,
      addonFeatureManager,
      registerValidateMethod,
      this.setEnableAdLogo
    );
    return this.creativeSetupStepModel;
  }

  abstract getCreativeSummaryModel(
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void
  ): CreativeSummaryStepModel;

  setCreative = (creative: CreativeFormData) => {
    if (this.event.count === 0) {
      // already unmount
      return;
    }
    this.creative = creative;
    this.updateState(false);
  };

  get state(): CreativeSetupFlowState {
    return {
      loading: this.loading,
      redirectData: this.redirectData,
      creative: this.creative,
      finished: this.finished,
      enableAdLogo: this.enableAdLogo
    };
  }

  setRedirectData(redirectData?: RedirectData) {
    this.redirectData = redirectData;
  }

  setFinishedRedirectData = (redirectData?: RedirectData) => {
    this.redirectData = redirectData;
    this.finished = true;
    this.updateState(false);
  };

  async initFormContentModel(formContentModel: FormContentModel) {
    this.updateState(true);
    await formContentModel.init();
    this.updateState(false);
  }

  async initCreativeApprovalConfigs() {
    this.creativeApprovalConfigs =
      await this.creativeManager.getCreativeApprovalConfigs();
  }

  getCreativeApprovalConfigs(creativeType: number) {
    return this.creativeApprovalConfigs.filter(
      (config) => config.bannerTypeId === creativeType
    );
  }

  async getFormContentModelOfType(
    type: number | undefined,
    advertiserId: number,
    needInit = true
  ): Promise<FormContentModel> {
    if (type === undefined) {
      return new ImageFormModel(this);
    }

    if (this.contentFormModelCache[type]) {
      return this.contentFormModelCache[type];
    }

    const canChangeBeacon = this.type === 'create';
    let contentFormModel;
    if (ChannelGroupSetting.isBeaconBannerType(type)) {
      contentFormModel = new LineMessageFormModel(
        advertiserId,
        5,
        [],
        false,
        canChangeBeacon
      );
    } else {
      switch (type) {
        case CreativeType.IMAGE:
          contentFormModel = new ImageFormModel(this);
          break;
        case CreativeType.NATIVE:
          contentFormModel = new NativeFormModel(
            this,
            this.nativeToBannertemplates
          );
          break;
        case CreativeType.VIDEO:
          contentFormModel = new VideoFormModel();
          break;
        case CreativeType.THIRD_PARTY:
          contentFormModel = new PPSFormModel();
          break;
        case CreativeType.HTML5:
          contentFormModel = new Html5FormModel(this);
          break;
        case CreativeType.COMBO:
          contentFormModel = new ComboFormModel();
          break;
        case CreativeType.CTV:
          contentFormModel = new CtvFormModel();
          break;
        case CreativeType.CUSTOM_BOTTOM:
        case CreativeType.CUSTOM_RECTANGLE:
          contentFormModel = new CustomLayoutFormModel(type);
          break;
        case CreativeType.SANDBOX_OUTDOOR:
          contentFormModel = new SandboxOutdoorFormModel(
            advertiserId,
            0,
            [],
            false,
            canChangeBeacon
          );
          break;
        case CreativeType.LINE_MESSAGE:
          contentFormModel = new LineMessageFormModel(
            advertiserId,
            5,
            [],
            false,
            canChangeBeacon
          );
          break;
        default:
          contentFormModel = new DynamicOutdoorFormModel(
            advertiserId,
            this.getCreativeApprovalConfigs(type),
            this.creativeManager.getCreativeTypeSetting(
              type
            ) as DynamicCreativeTypeSetting,
            this.forPmp,
            canChangeBeacon
          );
      }
    }
    needInit && (await this.initFormContentModel(contentFormModel));
    this.contentFormModelCache[type] = contentFormModel;
    return contentFormModel;
  }

  getSummaryModel(
    creativeBasicFormData: CreativeFormBasicData
  ): CreativeSummaryModel {
    const type = creativeBasicFormData.creativeType;
    const isBeaconBannerType = ChannelGroupSetting.isBeaconBannerType(type);
    if (isBeaconBannerType) {
      return new LineMessageSummaryModel(creativeBasicFormData);
    }
    switch (type) {
      case CreativeType.NATIVE:
        return new NativeSummaryModel(
          this.type,
          creativeBasicFormData,
          this.nativeToBannertemplates
        );
      case CreativeType.IMAGE:
        return new ImageSummaryModel(creativeBasicFormData);
      case CreativeType.VIDEO:
        return new VideoSummaryModel(creativeBasicFormData);
      case CreativeType.THIRD_PARTY:
        return new PPSSummaryModel(creativeBasicFormData);
      case CreativeType.HTML5:
        return new HTML5SummaryModel(creativeBasicFormData);
      case CreativeType.COMBO:
        return new ComboSummaryModel(creativeBasicFormData);
      case CreativeType.CTV:
        return new VideoSummaryModel(creativeBasicFormData);
      case CreativeType.CUSTOM_BOTTOM:
      case CreativeType.CUSTOM_RECTANGLE:
        return new CustomLayoutSummaryModel(creativeBasicFormData);
      case CreativeType.SANDBOX_OUTDOOR:
        return new SandboxOutdoorSummaryModel(creativeBasicFormData);
      case CreativeType.OUTDOOR_LINE_MESSAGE:
        return new OutdoorLineMessageSummaryModel(creativeBasicFormData);
      case CreativeType.LINE_MESSAGE:
        return new LineMessageSummaryModel(creativeBasicFormData);
      default:
        return new DynamicOutdoorSummaryModel(creativeBasicFormData);
    }
  }

  initAdvertiser = async (advertiserId: number) => {
    if (this.advertiser && this.advertiser.id === advertiserId) {
      return;
    }
    try {
      this.advertiser =
        await this.advertiserManager.getAdvertiser(advertiserId);
    } catch (e) {
      console.log(e);
    }
  };

  onChooseAdvertiser = async (advertiserId: number) => {
    this.updateState(true);
    this.contentFormModelCache = {};
    await this.initAdvertiser(advertiserId);
    this.creative &&
      this.advertiser &&
      _.set(this.creative, 'basic.tenmaxCategory', this.advertiser.category);
    this.updateState(false);
  };

  cancel = () => {
    const campaignParam = this.draftId
      ? `draftIds=${this.draftId}`
      : `campaignIds=${this.campaignId}`;
    const redirectPath =
      _.isNil(this.orderNumber) ||
      _.isNil(this.l1ObjectId) ||
      (_.isNil(this.campaignId) && _.isNil(this.draftId))
        ? '/creatives'
        : `/orders/${this.orderNumber}/campaign-groups/${this.l1ObjectId}?${campaignParam}&action=manage`;
    this.redirectData = {
      pathname: redirectPath
    };
    this.updateState(false);
  };

  setUpAdLogo = (
    creative: CreativeFormData,
    formContentModel?: FormContentModel
  ) => {
    this.enableAdLogo = formContentModel
      ? formContentModel.isAdLogoSupported(creative.basic)
      : false;
    if (this.enableAdLogo && _.isNil(creative.basic.adLogo)) {
      creative.basic = {
        ...creative.basic,
        adLogo: this.defaultAdLogo
      };
    }
  };

  onUnmount(handler) {
    this.event.remove(handler);
    this.redirectData = undefined;
    this.creative = undefined;
    this.creativeSetupStepModel = undefined;
    this.finished = false;
    this.uploadedFiles = {};
    this.contentFormModelCache = {};
    this.cache = {};
  }

  addCache(key: string, data: any) {
    this.cache[key] = data;
  }

  getCache(key: string) {
    return this.cache[key];
  }

  updateState(loading: boolean) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}

export class CreateCreativeSetupFlowPageModel extends DefaultCreativeSetupFlowPageModel {
  get type() {
    return 'create';
  }

  onChooseAdvertiser = async (advertiserId: number) => {
    this.updateState(true);
    this.contentFormModelCache = {};
    await this.initAdvertiser(advertiserId);
    if (!this.creative) {
      return;
    }
    try {
      const model = await this.getFormContentModelOfType(
        this.creative.basic.creativeType,
        advertiserId
      );
      this.advertiser &&
        _.set(this.creative, 'basic.tenmaxCategory', this.advertiser.category);
      _.set(
        this.creative,
        'basic.typeProperties',
        model.getInitTypeProperties()
      );
      _.set(this.creative, 'limitations', model.getInitLimitations());
      this.setUpAdLogo(this.creative, model);
      this.initCreative = JSON.parse(JSON.stringify(this.creative));
    } catch (e) {
      toast.error('init creative data of advertiser failed');
    }
    this.updateState(false);
  };

  async init() {
    this.updateState(true);
    try {
      const advertiserId = SessionStorageHelper.getNumberItem(
        SessionStorageItemKeys.ADVERTISER
      )!;
      const creativeType = this.getInitCreativeType();
      await this.initNativeBannerTemplates();
      await this.initCreativeApprovalConfigs();
      advertiserId && (await this.initAdvertiser(advertiserId));
      this.tenmaxCategories =
        await this.adRequestResourceManager.getTenmaxCategories();
      const model = advertiserId
        ? await this.getFormContentModelOfType(creativeType, advertiserId)
        : undefined;
      this.creative = {
        basic: {
          advertiserId,
          creativeType: creativeType,
          tenmaxCategory:
            this.tenmaxCategories && this.tenmaxCategories.length > 0
              ? this.tenmaxCategories[0].value.toString()
              : '',
          enableNativeBanner: isNative(creativeType) ? true : false
        },
        limitations: model
          ? model.getInitLimitations()
          : {
              include: [],
              exclude: [],
              nonPreferred: [],
              other: [],
              preferred: []
            }
      };

      this.advertiser &&
        _.set(this.creative, 'basic.tenmaxCategory', this.advertiser.category);
      _.set(
        this.creative,
        'basic.typeProperties',
        model ? model.getInitTypeProperties() : undefined
      );
      this.setUpAdLogo(this.creative, model);
      this.initCreative = JSON.parse(JSON.stringify(this.creative));
    } catch (e) {
      console.log(e);
    }
    this.updateState(false);
  }

  async getFormContentModelOfType(
    type: CreativeType,
    advertiserId: number
  ): Promise<FormContentModel> {
    if (this.contentFormModelCache[type]) {
      return this.contentFormModelCache[type];
    }
    let contentFormModel;
    if (type === CreativeType.IMAGE) {
      contentFormModel = new CreateImageFormModel(this);
    } else {
      contentFormModel = await super.getFormContentModelOfType(
        type,
        advertiserId,
        false
      );
    }
    await this.initFormContentModel(contentFormModel);
    this.contentFormModelCache[type] = contentFormModel;
    return contentFormModel;
  }

  getSummaryModel(
    creativeBasicFormData: CreativeFormBasicData
  ): CreativeSummaryModel {
    const type = creativeBasicFormData.creativeType;
    if (type === CreativeType.IMAGE) {
      return new CreateImageMultipleSummaryModel(creativeBasicFormData);
    }
    return super.getSummaryModel(creativeBasicFormData);
  }

  getCreativeSummaryModel(
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void
  ): CreativeSummaryStepModel {
    return new CreateCreativeSummaryStepModel(
      this.type,
      this.canChooseAdvertiser,
      goLast,
      goStep,
      this.getUploadedFileData,
      this.addUploadedFilesData,
      this.creativeManager,
      this.campaignId,
      this.orderNumber,
      this.l1Object,
      this.draftId
    );
  }
}

export class EditCreativeSetupFlowPageModel extends DefaultCreativeSetupFlowPageModel {
  isCreativeForPmp = false;

  constructor(
    public creativeId: number,
    ...args: ConstructorParameters<typeof DefaultCreativeSetupFlowPageModel>
  ) {
    super(...args);
  }

  get type() {
    return 'edit';
  }

  get forPmp() {
    return this.isCreativeForPmp;
  }

  async init() {
    this.updateState(true);
    try {
      await this.initL1Object();
      await this.initNativeBannerTemplates();
      await this.initCreativeApprovalConfigs();
      this.tenmaxCategories =
        await this.adRequestResourceManager.getTenmaxCategories();
      const creativeFromServer = await this.creativeManager.getCreative(
        this.creativeId
      );
      // ECOPD-5566：停用渠道底下的素材轉唯讀。素材列表的編輯入口已被收掉，
      // 這裡擋的是直接打 /creatives/:id/edit 網址進來的情況。
      //
      // 這一頁沒有 channelGroupId，改由素材類型反查：
      // `creative.basic.creativeType` 就是 bannerTypeId，而一個素材類型只屬於一個渠道
      // （這條領域事實由 `ChannelGroupSettings.test.ts` 的守門測試釘住）。
      const creativeChannelGroupId =
        ChannelGroupSetting.findChannelGroupIdByCreativeType(
          creativeFromServer.basic.creativeType
        );
      if (
        ChannelGroupSetting.isEffectivelyArchivedChannel(creativeChannelGroupId)
      ) {
        this.redirectData = {
          pathname: `/creatives/${this.creativeId}/edit/${ROUTE_PATH.ERROR403}`
        };
        this.updateState(false);
        return;
      }
      // ECOPD-5722：渠道的合成設定不可用時不給編輯。素材列表那側的編輯 icon 已經
      // disabled 並帶著同一句提示，這裡擋的是直接打網址進來的深連結。
      //
      // 導回素材列表而不是 403：403 的文案講的是沒有權限，對這個情境會誤導；
      // 導回列表則會看到那一列的編輯 icon 已經灰掉、hover 得到同一個原因。
      if (isCompositeSettingBlocked(creativeFromServer.basic.creativeType)) {
        toast.error(getCompositeSettingInvalidHint());
        this.redirectData = { pathname: '/creatives' };
        this.updateState(false);
        return;
      }
      if (isOneForAllCreativeType(creativeFromServer.basic.creativeType)) {
        this.redirectData = { pathname: '/creatives' };
        this.updateState(false);
        return;
      }
      this.isCreativeForPmp =
        !(await this.creativeManager.checkCreativeSupportSchedule(
          this.creativeId
        ));
      const creativeTenmaxCategory = creativeFromServer.basic.tenmaxCategory;
      if (
        !this.tenmaxCategories.find(
          (tenmaxCategory) => tenmaxCategory.value === creativeTenmaxCategory
        )
      ) {
        this.tenmaxCategories.push({
          label: creativeTenmaxCategory,
          value: creativeTenmaxCategory
        });
      }
      const { creativeId, advertiserId, creativeType } =
        creativeFromServer.basic;
      await this.initAdvertiser(advertiserId);
      const contentFormModel = await this.getFormContentModelOfType(
        creativeType,
        advertiserId
      );
      if (!contentFormModel) {
        this.redirectData = {
          pathname: `/creatives/${creativeId}/edit/${ROUTE_PATH.ERROR404}`
        };
        this.updateState(false);
        return;
      }
      this.creative = contentFormModel.getFormModelData(creativeFromServer);
      this.setUpAdLogo(this.creative, contentFormModel);
      this.initCreative = JSON.parse(JSON.stringify(this.creative));
    } catch (e) {
      console.log(e);
    }
    this.updateState(false);
  }

  getCreativeSummaryModel(
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void
  ): CreativeSummaryStepModel {
    return new EditCreativeSummaryStepModel(
      this.type,
      this.canChooseAdvertiser,
      goLast,
      goStep,
      this.getUploadedFileData,
      this.addUploadedFilesData,
      this.creativeManager,
      this.campaignId,
      this.orderNumber,
      this.l1Object,
      this.draftId
    );
  }
}

abstract class CreateCreativeFromL2ObjectSetupFlowPageModel extends CreateCreativeSetupFlowPageModel {
  constructor(
    canChooseAdvertiser: boolean,
    advertisers: Array<SelectOptions>,
    addonFeatureManager: AddonFeatureManager,
    defaultAdLogo: AdLogo,
    campaignId?: string,
    orderNumber?: string,
    l1ObjectId?: string,
    draftId?: string,
    adRequestResourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager(),
    private orderManager: OrderManager = new DefaultOrderManager(),
    protected l1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager()
  ) {
    super(
      canChooseAdvertiser,
      advertisers,
      addonFeatureManager,
      defaultAdLogo,
      campaignId,
      orderNumber,
      l1ObjectId,
      draftId,
      adRequestResourceManager
    );
  }

  abstract get isChannelSupport();

  abstract get supportedCreativeType();

  abstract initCampaign();

  async init() {
    if (!this.orderNumber) {
      return;
    }
    this.updateState(true);
    try {
      await this.initL1Object();
      await this.initCampaign();
      await this.initNativeBannerTemplates();
      await this.initCreativeApprovalConfigs();
      const order = await this.orderManager.getOrder(this.orderNumber);
      this.tenmaxCategories =
        await this.adRequestResourceManager.getTenmaxCategories();
      const advertiserId = order.advertiserId;
      await this.initAdvertiser(advertiserId);
      const creativeType = this.supportedCreativeType[0];
      const model = await this.getFormContentModelOfType(
        creativeType,
        advertiserId
      );
      this.creative = {
        basic: {
          advertiserId,
          creativeType,
          tenmaxCategory:
            this.tenmaxCategories && this.tenmaxCategories.length > 0
              ? this.tenmaxCategories[0].value.toString()
              : '',
          enableNativeBanner: isNative(creativeType) ? true : false
        },
        limitations: model ? model.getInitLimitations() : {}
      };
      this.advertiser &&
        _.set(this.creative, 'basic.tenmaxCategory', this.advertiser.category);
      _.set(
        this.creative,
        'basic.typeProperties',
        model ? model.getInitTypeProperties() : undefined
      );
      this.setUpAdLogo(this.creative, model);
      this.initCreative = JSON.parse(JSON.stringify(this.creative));
    } catch (e) {
      console.error(e);
    }
    this.updateState(false);
  }

  abstract getCreativeSummaryModel(
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void
  ): CreativeSummaryStepModel;
}

export class DefaultCreateCreativeFromL2ObjectSetupFlowPageModel extends CreateCreativeFromL2ObjectSetupFlowPageModel {
  campaign?: RtbCampaign;
  channelGroupSetting: ChannelGroupSetting;

  constructor(
    canChooseAdvertiser: boolean,
    advertisers: Array<SelectOptions>,
    addonFeatureManager: AddonFeatureManager,
    defaultAdLogo: AdLogo,
    private l1ObjectChannel: string,
    campaignId?: string,
    orderNumber?: string,
    l1ObjectId?: string,
    draftId?: string,
    adRequestResourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager(),
    private draftManager: DraftManager = new RtbCampaignDraftManager()
  ) {
    super(
      canChooseAdvertiser,
      advertisers,
      addonFeatureManager,
      defaultAdLogo,
      campaignId,
      orderNumber,
      l1ObjectId,
      draftId,
      adRequestResourceManager
    );
    this.channelGroupSetting = ChannelGroupSetting.fromKey(
      this.l1ObjectChannel
    );
  }

  async initCampaign() {
    if (this.draftId) {
      try {
        this.campaign = await this.draftManager.getDraft(
          this.channelGroupSetting.config.id,
          this.draftId
        );
      } catch (e) {
        console.log(e);
      }
      return;
    }
    if (!this.campaignId) {
      return;
    }
    try {
      this.campaign = await this.campaignManager.getCampaign(this.campaignId);
    } catch (e) {
      console.log(e);
    }
  }

  get isChannelSupport() {
    return this.l1ObjectManager.isChannelAllowed(
      this.l1ObjectChannel,
      this.addonFeatureManager
    );
  }

  get supportedCreativeType() {
    let result: CreativeType[] = [];
    if (
      !this.campaign ||
      (!this.campaign.basic.adType && !this.campaign.basic.adTypeId) ||
      !this.isChannelSupport
    ) {
      return result;
    }

    const adTypeSetting =
      this.channelGroupSetting?.config.campaignAdTypeSettings.find(
        (setting) => {
          if (this.campaign!.basic.adTypeId) {
            return setting.adTypeId === this.campaign!.basic.adTypeId;
          }
          return setting.name === this.campaign!.basic.adType;
        }
      );
    const creativeTypes =
      adTypeSetting?.bannerTypes.map(
        (creativeTypeSetting) => creativeTypeSetting.bannerTypeId
      ) || [];
    creativeTypes.forEach((creativeType) => {
      !isOneForAllCreativeType(creativeType) &&
        this.creativeManager.isCreativeTypeSupport(
          creativeType,
          this.addonFeatureManager
        ) &&
        result.push(creativeType);
    });
    return result.filter((type) => this.validCreativeTypes.includes(type));
  }

  getCreativeSummaryModel(
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void
  ): CreativeSummaryStepModel {
    return new CreateCreativeAndBindSummaryStepModel(
      this.type,
      this.canChooseAdvertiser,
      goLast,
      goStep,
      this.getUploadedFileData,
      this.addUploadedFilesData,
      this.creativeManager,
      this.campaignId,
      this.orderNumber,
      this.l1Object,
      this.draftId
    );
  }
}

export class CreateCreativeInL2ObjectSetupFlowPageModel extends CreateCreativeSetupFlowPageModel {
  channelGroupSetting: ChannelGroupSetting;

  constructor(
    canChooseAdvertiser: boolean,
    advertisers: Array<SelectOptions>,
    addonFeatureManager: AddonFeatureManager,
    defaultAdLogo: AdLogo,
    public campaign: RtbCampaign,
    public l1Object: L1Object,
    private order: Order
  ) {
    super(canChooseAdvertiser, advertisers, addonFeatureManager, defaultAdLogo);
    this.channelGroupSetting = ChannelGroupSetting.fromId(
      l1Object.channelGroupId
    );
  }

  get supportedCreativeType() {
    let result: CreativeType[] = [];
    if (
      !this.campaign ||
      (!this.campaign.basic.adType && !this.campaign.basic.adTypeId)
    ) {
      return result;
    }

    let creativeTypes: number[] = [];
    if (this.forPmp) {
      creativeTypes =
        this.channelGroupSetting.config.campaignAdTypeSettings.flatMap(
          (setting) =>
            setting.bannerTypes.map((bannerType) => bannerType.bannerTypeId)
        );
    } else {
      const adTypeSetting =
        this.channelGroupSetting.config.campaignAdTypeSettings.find(
          (setting) => {
            if (this.campaign.basic.adTypeId) {
              return setting.adTypeId === this.campaign.basic.adTypeId;
            }
            return setting.name === this.campaign.basic.adType;
          }
        );

      creativeTypes =
        adTypeSetting?.bannerTypes.map(
          (creativeTypeSetting) => creativeTypeSetting.bannerTypeId
        ) || [];
    }

    creativeTypes.forEach((creativeType) => {
      !isOneForAllCreativeType(creativeType) &&
        this.creativeManager.isCreativeTypeSupport(
          creativeType,
          this.addonFeatureManager
        ) &&
        result.push(creativeType);
    });
    return result.filter((type) => this.validCreativeTypes.includes(type));
  }

  get forPmp() {
    return (
      this.campaign.basic.adType === 'PMP' || this.campaign.basic.isPmp || false
    );
  }

  async init() {
    this.updateState(true);
    try {
      await this.initCreativeApprovalConfigs();
      this.tenmaxCategories =
        await this.adRequestResourceManager.getTenmaxCategories();
      const campaignName = this.campaign.basic.name;
      const advertiserId = this.order.advertiserId;
      const creativeType = this.supportedCreativeType[0];
      await this.initAdvertiser(advertiserId);
      const model = await this.getFormContentModelOfType(
        creativeType,
        advertiserId
      );
      this.creative = {
        basic: {
          name: campaignName,
          advertiserId,
          creativeType,
          tenmaxCategory:
            this.tenmaxCategories && this.tenmaxCategories.length > 0
              ? this.tenmaxCategories[0].value.toString()
              : ''
        }
      };

      this.advertiser &&
        _.set(this.creative, 'basic.tenmaxCategory', this.advertiser.category);
      _.set(
        this.creative,
        'basic.typeProperties',
        model ? model.getInitTypeProperties() : undefined
      );
      this.initCreative = JSON.parse(JSON.stringify(this.creative));
    } catch (e) {
      console.error(e);
    }
    this.updateState(false);
  }

  getSummaryModel(
    creativeBasicFormData: CreativeFormBasicData
  ): CreativeSummaryModel {
    const type = creativeBasicFormData.creativeType;
    if (type === CreativeType.IMAGE) {
      return new ImageSummaryModel(creativeBasicFormData);
    }
    return super.getSummaryModel(creativeBasicFormData);
  }
}

export class BindCreativeInL2ObjectSetupFlowPageModel extends DefaultCreativeSetupFlowPageModel {
  bindCreativeStateContentModel: BindCreativeStateContentModel;
  channelGroupSetting: ChannelGroupSetting;

  constructor(
    canChooseAdvertiser: boolean,
    advertisers: Array<SelectOptions>,
    addonFeatureManager: AddonFeatureManager,
    defaultAdLogo: AdLogo,
    public campaign: RtbCampaign,
    public order: Order,
    l1Object: L1Object,
    isPmp: boolean
  ) {
    super(canChooseAdvertiser, advertisers, addonFeatureManager, defaultAdLogo);
    const { startDate, endDate, name, adType } = this.campaign.basic;
    this.bindCreativeStateContentModel =
      new DefaultBindCreativeStateContentModel(
        [
          {
            id: -1,
            startDate,
            endDate,
            name,
            adType,
            channel: l1Object.channel,
            channelGroupId: l1Object.channelGroupId,
            billingEvent: 'UNSPECIFIED',
            isPmp
          }
        ],
        order.advertiserId,
        undefined,
        true
      );
    this.channelGroupSetting = ChannelGroupSetting.fromId(
      l1Object.channelGroupId
    );
  }

  get type() {
    return 'bind';
  }

  get supportedCreativeType() {
    return [];
  }

  async init() {
    /* no-op */
  }

  get forPmp() {
    return this.campaign.basic.isPmp || false;
  }

  getSummaryModel(
    creativeBasicFormData: CreativeFormBasicData
  ): CreativeSummaryModel {
    const type = creativeBasicFormData.creativeType;
    if (type === CreativeType.IMAGE) {
      return new ImageSummaryModel(creativeBasicFormData);
    }
    return super.getSummaryModel(creativeBasicFormData);
  }

  getCreativeSummaryModel(
    goLast: () => void,
    goStep: (stepIndex: number, subStepIndex?: number) => void
  ): CreativeSummaryStepModel {
    return new EditCreativeSummaryStepModel(
      this.type,
      this.canChooseAdvertiser,
      goLast,
      goStep,
      this.getUploadedFileData,
      this.addUploadedFilesData,
      this.creativeManager,
      this.campaignId,
      this.orderNumber,
      this.l1Object,
      this.draftId
    );
  }
}
